import { useEffect } from 'react'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: string
  siteName?: string
  author?: string
  structuredData?: object
}

const defaultSEO = {
  title: 'Binder Tech Solutions - AI-Powered Location Intelligence & Geofencing',
  description: 'Transform your business with BLIS (Binder Location Intelligence System) and IZG (Intelligent Zoning & Geofencing) platforms. AI-powered location scoring, smart zoning, and real-time asset monitoring.',
  keywords: 'location intelligence, geofencing, AI location scoring, business intelligence, asset tracking, smart zoning, location analytics, SaaS platform',
  image: '/og-image.jpg',
  url: 'https://bindertech.com',
  type: 'website',
  siteName: 'Binder Tech Solutions',
  author: 'Binder Tech Solutions',
}

export const useSEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  siteName,
  author,
  structuredData,
}: SEOProps = {}) => {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${defaultSEO.siteName}` : defaultSEO.title
    const metaDescription = description || defaultSEO.description
    const metaKeywords = keywords || defaultSEO.keywords
    const metaImage = image || defaultSEO.image
    const metaUrl = url || defaultSEO.url
    const metaType = type || defaultSEO.type
    const metaSiteName = siteName || defaultSEO.siteName
    const metaAuthor = author || defaultSEO.author

    // Update document title
    document.title = fullTitle

    // Helper function to update or create meta tag
    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement
      if (!element) {
        element = document.createElement('meta')
        element.setAttribute(attribute, name)
        document.head.appendChild(element)
      }
      element.setAttribute('content', content)
    }

    // Basic meta tags
    updateMetaTag('description', metaDescription)
    updateMetaTag('keywords', metaKeywords)
    updateMetaTag('author', metaAuthor)

    // Open Graph meta tags
    updateMetaTag('og:title', fullTitle, 'property')
    updateMetaTag('og:description', metaDescription, 'property')
    updateMetaTag('og:image', metaImage, 'property')
    updateMetaTag('og:url', metaUrl, 'property')
    updateMetaTag('og:type', metaType, 'property')
    updateMetaTag('og:site_name', metaSiteName, 'property')

    // Twitter Card meta tags
    updateMetaTag('twitter:card', 'summary_large_image')
    updateMetaTag('twitter:title', fullTitle)
    updateMetaTag('twitter:description', metaDescription)
    updateMetaTag('twitter:image', metaImage)

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', metaUrl)

    // Structured Data (JSON-LD)
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement
      if (!script) {
        script = document.createElement('script')
        script.setAttribute('type', 'application/ld+json')
        document.head.appendChild(script)
      }
      script.textContent = JSON.stringify(structuredData)
    }
  }, [title, description, keywords, image, url, type, siteName, author, structuredData])
}

